import { BackendModule, InitOptions, ReadCallback, Services } from 'i18next';
import { HygraphClient } from './HygraphClient';

interface BackendOptions {
  reloadInterval: false | number;
  hygraphUrl: string;
  hygraphStage: string;
  aggressiveFetch?: boolean;
  hygraphUsername?: string;
  hygraphPassword?: string;
}

type BackendOptionsOrPromise = BackendOptions | Promise<BackendOptions>;

// eslint-disable-next-line @typescript-eslint/naming-convention
class i18nextHygraphPlugin implements BackendModule<BackendOptionsOrPromise> {
  static type = 'backend' as const;
  type = 'backend' as const;
  private hygraphClient?: HygraphClient;
  private readyPromise?: Promise<BackendOptions>;
  public backendOptions?: BackendOptions;
  constructor(
    public services: Services,
    backendOptionsOrPromise: BackendOptionsOrPromise,
    public i18nextOptions: InitOptions = {},
  ) {
    void this.init(services, backendOptionsOrPromise, i18nextOptions);
  }
  async init(
    services: Services,
    backendOptionsOrPromise: BackendOptionsOrPromise,
    i18nextOptions: InitOptions,
  ): Promise<void> {
    if (services === undefined) return;
    this.services = services;
    this.i18nextOptions = i18nextOptions;
    this.readyPromise = Promise.resolve(backendOptionsOrPromise);
    this.backendOptions = await this.readyPromise;

    if (this.backendOptions?.aggressiveFetch !== false) {
      this.reload();
    }
    if (this.backendOptions?.reloadInterval) {
      setInterval(() => this.reload(), this.backendOptions.reloadInterval);
    }
  }

  private getHygraphClient(): HygraphClient {
    if (!this.backendOptions) {
      throw 'Cannot initialize i18n hygraph client';
    }
    if (!this.hygraphClient) {
      if (
        this.backendOptions.hygraphUsername &&
        this.backendOptions.hygraphPassword
      ) {
        this.hygraphClient = new HygraphClient(
          this.backendOptions.hygraphUrl,
          this.backendOptions.hygraphStage,
          {
            username: this.backendOptions.hygraphUsername,
            password: this.backendOptions.hygraphPassword,
          },
        );
      } else {
        this.hygraphClient = new HygraphClient(
          this.backendOptions.hygraphUrl,
          this.backendOptions.hygraphStage,
        );
      }
    }
    return this.hygraphClient;
  }

  reload(): void {
    console.info('hygraph i18n reload called at: ', new Date());
    const languages = Array.isArray(this.i18nextOptions.preload)
      ? (this.i18nextOptions.preload as string[])
      : [this.i18nextOptions.lng as string];

    const namespaces = Array.isArray(this.i18nextOptions.ns)
      ? (this.i18nextOptions.ns as string[])
      : [this.i18nextOptions.ns as string];

    languages.forEach((language) => {
      namespaces.forEach((ns) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
        this.services.backendConnector.read(
          language,
          ns,
          'read',
          null,
          null,
          (err: unknown, data: unknown) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
            this.services.backendConnector.loaded(
              `${language}|${ns}`,
              err,
              data,
            );
          },
        );
      });
    });
  }

  read(
    language: string,
    // @ts-expect-error unused, but defined by base class
    namespace: string,
    callback: ReadCallback,
  ): void {
    this.readyPromise?.then(() => {
      this.getHygraphClient()
        .fetchStringsForLocale(language)
        .then((results) => {
          callback(null, results);
        })
        .catch((err) => {
          callback(err, true);
        });
    });
  }
}

export { i18nextHygraphPlugin };
